/**
 * MyEvidenceRequests — /w/:slug/app/evidence-requests
 *
 * PR-Cluster-6-d (docs/ux-design-plan.md §10) — supplier queue view
 * showing every evidence request an assessor routed to the current
 * signed-in workspace member, across every control they own.
 *
 * Complements PR-Cluster-6-c's per-item inline section: that surface
 * answers "what did the assessor ask about THIS control?" — this
 * surface answers "what's on my plate right now, across every
 * control I own?" Marcus / Nadia's daily landing when the 3PAO is
 * mid-engagement.
 *
 * Backend uses the pre-existing `?assignee=me&status=X` filter on
 * GET /api/w/:slug/evidence-requests (server-side scoping to
 * routed_to_user_id === session user).
 *
 * Deferred:
 *   - Group by engagement (currently flat list; a Datadog-style
 *     grouping tier + collapse-per-engagement lands when Cluster 6
 *     R3 mockups converge)
 *   - Cross-workspace variant for Consultant persona (Nadia's SMB
 *     multi-client fold — the /assessor cross-workspace inbox
 *     pattern applied to a supplier persona)
 *   - Priority / SLA badges (needs a server-side `days_open`
 *     computation)
 */

import { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, EmptyState, PageHeader, Spinner } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import {
  listMyEvidenceRequests,
  type EvidenceRequestRow,
  type EvidenceRequestSeverity,
  type EvidenceRequestStatus,
} from '../lib/api';
import { fmtDate } from '../lib/format';

const STATUS_FILTERS: Array<{ value: EvidenceRequestStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'evidence_provided', label: 'Provided' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_FILTER_VALUES = STATUS_FILTERS.map((f) => f.value);

function isStatusFilter(input: string | null): input is EvidenceRequestStatus {
  return input !== null && (STATUS_FILTER_VALUES as string[]).includes(input);
}

export function MyEvidenceRequests() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  // PR-Cluster-1-f (follow-up): filter state persists in the URL
  // via ?status=<value>. Refresh keeps the filter; a link like
  // /w/:slug/app/evidence-requests?status=closed opens the queue
  // pre-filtered. Invalid values fall back to 'open' (the default
  // supplier-attention set) rather than throwing.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStatus = searchParams.get('status');
  const status: EvidenceRequestStatus = isStatusFilter(rawStatus) ? rawStatus : 'open';
  const setStatus = (next: EvidenceRequestStatus) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        // Default is 'open' — clear the param so the canonical URL
        // for the default view has no query string.
        if (next === 'open') {
          params.delete('status');
        } else {
          params.set('status', next);
        }
        return params;
      },
      { replace: true },
    );
  };

  const requestsQuery = useQuery({
    queryKey: queryKeys.myEvidenceRequests(slug ?? '', status),
    queryFn: () => listMyEvidenceRequests(slug!, status),
    enabled: !!slug,
  });
  const requests = {
    data: requestsQuery.data ?? null,
    error: requestsQuery.error?.message ?? null,
    loading: requestsQuery.isLoading,
  };

  const bySeverity = useMemo(() => {
    const rows = requests.data?.requests ?? [];
    const critical = rows.filter((r) => r.severity === 'critical_finding').length;
    const clarification = rows.filter((r) => r.severity === 'clarification').length;
    return { critical, clarification, total: rows.length };
  }, [requests.data]);

  const prefix = useCallback(
    () => (workspaceCtx ? `/w/${workspaceCtx.workspace.slug}/app` : '/app' /* scope-check-ignore: legacy fallback */),
    [workspaceCtx],
  );

  if (!slug) return null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '68rem' }}>
      <PageHeader
        title="Assessor requests"
        subtitle="Evidence requests routed to you across every control you own."
      />

      <ScoreboardStrip counts={bySeverity} status={status} />

      <FilterBar current={status} onChange={setStatus} />

      {requests.loading ? (
        <Spinner label="Loading requests" />
      ) : requests.error ? (
        <Alert variant="error">{requests.error}</Alert>
      ) : !requests.data || requests.data.requests.length === 0 ? (
        <EmptyState
          title={status === 'open' ? 'Nothing on your plate' : `No ${status.replace('_', ' ')} requests`}
          description={
            status === 'open'
              ? 'The assessor hasn\'t routed any evidence requests to you yet. Requests appear here the moment they\'re opened.'
              : `Switch the filter to see requests in a different state.`
          }
        />
      ) : (
        <ol
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          {requests.data.requests.map((r) => (
            <RequestRow key={r.request_id} row={r} appPrefix={prefix()} />
          ))}
        </ol>
      )}
    </div>
  );
}

// ---- Scoreboard strip ------------------------------------------------

function ScoreboardStrip({
  counts,
  status,
}: {
  counts: { critical: number; clarification: number; total: number };
  status: EvidenceRequestStatus;
}) {
  return (
    <section
      aria-label="Request summary"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
        gap: 'var(--space-3)',
      }}
    >
      <Tile
        label={`${status.replace('_', ' ')} total`}
        value={counts.total}
        tone={counts.total === 0 ? 'muted' : 'accent'}
      />
      <Tile
        label="Critical findings"
        value={counts.critical}
        tone={counts.critical > 0 ? 'danger' : 'muted'}
      />
      <Tile
        label="Clarifications"
        value={counts.clarification}
        tone={counts.clarification > 0 ? 'warning' : 'muted'}
      />
    </section>
  );
}

function Tile(props: {
  label: string;
  value: number;
  tone: 'muted' | 'accent' | 'warning' | 'danger';
}) {
  const toneStyle: Record<typeof props.tone, { bg: string; border: string; label: string }> = {
    muted: { bg: 'var(--surface)', border: 'var(--border)', label: 'var(--text-muted)' },
    accent: { bg: 'var(--surface)', border: 'var(--border)', label: 'var(--text-muted)' },
    warning: { bg: 'var(--warning-bg)', border: 'var(--band-amber)', label: 'var(--warning-fg)' },
    danger: { bg: 'var(--danger-bg)', border: 'var(--band-red)', label: 'var(--danger-fg)' },
  };
  const t = toneStyle[props.tone];
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        border: `1px solid ${t.border}`,
        borderRadius: 'var(--radius-card)',
        background: t.bg,
        display: 'grid',
        gap: '2px',
        textTransform: 'capitalize',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: t.label,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
        }}
      >
        {props.label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-xl)',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--text)',
        }}
      >
        {props.value}
      </span>
    </div>
  );
}

// ---- Filter bar ------------------------------------------------------

function FilterBar({
  current,
  onChange,
}: {
  current: EvidenceRequestStatus;
  onChange: (s: EvidenceRequestStatus) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Filter by status"
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
      }}
    >
      {STATUS_FILTERS.map((f) => {
        const on = f.value === current;
        return (
          <button
            key={f.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(f.value)}
            style={{
              appearance: 'none',
              padding: '6px var(--space-3)',
              border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
              background: on ? 'var(--accent)' : 'transparent',
              color: on ? 'var(--text-inverse)' : 'var(--text-muted)',
              borderRadius: '999px',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: on ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

// ---- Request row -----------------------------------------------------

function RequestRow({ row, appPrefix }: { row: EvidenceRequestRow; appPrefix: string }) {
  return (
    <li>
      <Link
        to={`${appPrefix}/register/${encodeURIComponent(row.register_item_id)}`}
        style={{
          display: 'grid',
          gap: 'var(--space-2)',
          padding: 'var(--space-3) var(--space-4)',
          border: '1px solid var(--border)',
          borderLeftWidth: '3px',
          borderLeftColor: severityBorder(row.severity, row.status),
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        <header style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <SeverityChip severity={row.severity} />
          <StatusChip status={row.status} />
          <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }} title={row.register_item_id}>
            {row.register_item_id}
          </span>
          <time dateTime={row.opened_at} style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
            opened {fmtDate(row.opened_at)}
          </time>
        </header>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {row.reason_text}
        </p>
        <footer style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          <span style={{ fontFamily: 'var(--font-mono)' }} title={row.engagement_id}>
            engagement {row.engagement_id.slice(0, 12)}…
          </span>
          <span style={{ marginLeft: 'auto', color: 'var(--accent)', fontWeight: 500 }}>
            Open register item →
          </span>
        </footer>
      </Link>
    </li>
  );
}

function SeverityChip({ severity }: { severity: EvidenceRequestSeverity }) {
  const tone: Record<EvidenceRequestSeverity, { bg: string; fg: string; label: string }> = {
    clarification: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)', label: 'Clarification' },
    critical_finding: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)', label: 'Critical finding' },
  };
  const t = tone[severity];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function StatusChip({ status }: { status: EvidenceRequestStatus }) {
  const tone: Record<EvidenceRequestStatus, { bg: string; fg: string; label: string }> = {
    open: { bg: 'var(--surface-alt)', fg: 'var(--text-muted)', label: 'Open' },
    evidence_provided: { bg: 'var(--accent)', fg: 'var(--text-inverse)', label: 'Provided' },
    closed: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', label: 'Closed' },
  };
  const t = tone[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function severityBorder(severity: EvidenceRequestSeverity, status: EvidenceRequestStatus): string {
  if (status === 'closed') return 'var(--border)';
  if (severity === 'critical_finding') return 'var(--band-red)';
  return 'var(--band-amber)';
}

